import { AlertTriangle, CheckCircle2, HelpCircle, Info, Loader2, XCircle, type LucideIcon } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { locale, t } from "../i18n";

/* ------------------------------------------------------------------ formatting */

export function fmtTs(ts?: string | null): string {
  if (!ts || ts.length < 8) return ts || "—";
  const t = ts.padEnd(14, "0");
  return `${t.slice(0, 4)}-${t.slice(4, 6)}-${t.slice(6, 8)} ${t.slice(8, 10)}:${t.slice(10, 12)}`;
}

export function fmtDay(ts?: string | null): string {
  if (!ts || ts.length < 8) return "—";
  const d = new Date(Date.UTC(+ts.slice(0, 4), +ts.slice(4, 6) - 1, +ts.slice(6, 8)));
  return d.toLocaleDateString(locale(), { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
}

function toDate(iso?: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso.endsWith("Z") || /[+-]\d\d:\d\d$/.test(iso) ? iso : iso + "Z");
  return isNaN(d.getTime()) ? null : d;
}

export function fmtDate(iso?: string | null): string {
  const d = toDate(iso);
  return d ? d.toLocaleString(locale(), { dateStyle: "medium", timeStyle: "short" }) : "—";
}

export function fmtRelative(iso?: string | null): string {
  const d = toDate(iso);
  if (!d) return "—";
  const diff = d.getTime() - Date.now();
  const abs = Math.abs(diff);
  const units: [number, Intl.RelativeTimeFormatUnit][] = [[86400000, "day"], [3600000, "hour"], [60000, "minute"]];
  const rtf = new Intl.RelativeTimeFormat(locale(), { numeric: "auto" });
  for (const [ms, unit] of units) if (abs >= ms) return rtf.format(Math.round(diff / ms), unit);
  return diff >= 0 ? t("in a moment") : t("just now");
}

/** "2 h 15 min", "45 min", "less than a minute". */
export function fmtDuration(seconds?: number | null): string {
  if (seconds === null || seconds === undefined || !isFinite(seconds)) return "—";
  const m = Math.round(seconds / 60);
  if (m < 1) return t("less than a minute");
  if (m < 60) return t("{m} min", { m });
  const h = Math.floor(m / 60);
  return m % 60 ? t("{h} h {m} min", { h, m: m % 60 }) : t("{h} h", { h });
}

export function fmtBytes(n?: number | null): string {
  if (n === null || n === undefined) return "—";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
}

export function fmtNum(n?: number | null): string {
  return n === null || n === undefined ? "—" : n.toLocaleString(locale());
}

/* ------------------------------------------------------------------ status */

type Tone = "neutral" | "info" | "progress" | "success" | "warning" | "danger" | "violet";

const STATUS: Record<string, { label: string; tone: Tone }> = {
  // jobs
  queued: { label: "Queued", tone: "neutral" },
  running: { label: "Running", tone: "progress" },
  paused: { label: "Paused", tone: "warning" },
  completed: { label: "Completed", tone: "success" },
  failed: { label: "Failed", tone: "danger" },
  cancelled: { label: "Cancelled", tone: "neutral" },
  partial_success: { label: "Partly downloaded", tone: "warning" },
  // domains
  new: { label: "Not analyzed", tone: "neutral" },
  discovering: { label: "Analyzing", tone: "progress" },
  analyzed: { label: "Ready to recover", tone: "info" },
  recovering: { label: "Recovering", tone: "progress" },
  recovered: { label: "Recovered", tone: "success" },
  error: { label: "Needs attention", tone: "danger" },
  // files
  pending: { label: "Pending", tone: "neutral" },
  downloaded: { label: "Downloaded", tone: "success" },
  missing: { label: "Not archived", tone: "warning" },
  skipped: { label: "Skipped", tone: "neutral" },
  excluded_spam: { label: "Spam – excluded", tone: "danger" },
  // validation
  verified: { label: "OK", tone: "success" },
  recovered_not_validated: { label: "Not checked", tone: "neutral" },
  partially_recovered: { label: "Partial", tone: "warning" },
  missing_from_archive: { label: "Not archived", tone: "warning" },
  download_failed: { label: "Failed", tone: "danger" },
  unsupported_dynamic: { label: "Dynamic feature", tone: "violet" },
};

const TONE: Record<Tone, string> = {
  neutral: "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
  info: "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950 dark:text-sky-300 dark:ring-sky-900",
  progress: "bg-brand-50 text-brand-700 ring-brand-200 dark:bg-brand-900/40 dark:text-brand-200 dark:ring-brand-900",
  success: "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:ring-emerald-900",
  warning: "bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:ring-amber-900",
  danger: "bg-red-50 text-red-700 ring-red-200 dark:bg-red-950 dark:text-red-300 dark:ring-red-900",
  violet: "bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-950 dark:text-violet-300 dark:ring-violet-900",
};
const DOT: Record<Tone, string> = {
  neutral: "bg-slate-400", info: "bg-sky-500", progress: "bg-brand-500 animate-pulse", success: "bg-emerald-500",
  warning: "bg-amber-500", danger: "bg-red-500", violet: "bg-violet-500",
};

export function statusLabel(value?: string | null): string {
  return (value && STATUS[value] && t(STATUS[value].label)) || (value ?? "—").replace(/_/g, " ");
}

export function Badge({ value, title }: { value: string | null | undefined; title?: string }) {
  if (!value) return <span className="muted">—</span>;
  const s = STATUS[value] ?? { label: value.replace(/_/g, " "), tone: "neutral" as Tone };
  return (
    <span title={title} className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${TONE[s.tone]}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${DOT[s.tone]}`} />
      {t(s.label)}
    </span>
  );
}

/* ------------------------------------------------------------------ building blocks */

export function Progress({ done, total, failed = 0, compact = false }: { done: number; total: number; failed?: number; compact?: boolean }) {
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const fpct = total > 0 ? Math.min(100, (failed / total) * 100) : 0;
  return (
    <div className="w-full">
      <div className={`flex overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800 ${compact ? "h-1.5" : "h-2"}`} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className="bg-brand-500 transition-all duration-500" style={{ width: `${Math.max(0, pct - fpct)}%` }} />
        <div className="bg-red-500 transition-all duration-500" style={{ width: `${fpct}%` }} />
      </div>
      {!compact && (
        <div className="mt-1.5 flex justify-between text-xs muted">
          <span>{total > 0 ? t("{done} of {total}", { done: fmtNum(done), total: fmtNum(total) }) : done > 0 ? t("{n} processed", { n: fmtNum(done) }) : t("Starting…")}</span>
          <span>{total > 0 ? `${pct}%` : ""}{failed > 0 && <span className="ml-2 text-red-600">{t("{n} failed", { n: fmtNum(failed) })}</span>}</span>
        </div>
      )}
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 py-6 text-sm muted" role="status">
      <Loader2 className="h-4 w-4 animate-spin" /> {label ?? t("Loading…")}
    </div>
  );
}

export function Skeleton({ className = "h-4 w-full" }: { className?: string }) {
  return <div className={`animate-pulse rounded bg-slate-200 dark:bg-slate-800 ${className}`} />;
}

const BANNER: Record<string, { icon: LucideIcon; cls: string }> = {
  info: { icon: Info, cls: "border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-900 dark:bg-sky-950/60 dark:text-sky-100" },
  success: { icon: CheckCircle2, cls: "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-100" },
  warning: { icon: AlertTriangle, cls: "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/60 dark:text-amber-100" },
  danger: { icon: XCircle, cls: "border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950/60 dark:text-red-100" },
};

export function Banner({ tone = "info", title, children, action }: { tone?: keyof typeof BANNER; title?: ReactNode; children?: ReactNode; action?: ReactNode }) {
  const { icon: Icon, cls } = BANNER[tone];
  return (
    <div role={tone === "danger" ? "alert" : "status"} className={`flex flex-col gap-3 rounded-xl border p-4 text-sm sm:flex-row sm:items-center ${cls}`}>
      <Icon className="h-5 w-5 shrink-0" />
      <div className="min-w-0 flex-1">
        {title && <div className="font-medium">{title}</div>}
        {children && <div className={title ? "mt-0.5 opacity-90" : ""}>{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function ErrorNote({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const msg = error instanceof Error ? error.message : String(error);
  return (
    <Banner tone="danger" action={onRetry && <button className="btn-secondary btn-sm" onClick={onRetry}>{t("Try again")}</button>}>
      {msg}
    </Banner>
  );
}

export function EmptyState({ icon: Icon, title, children, action }: { icon?: LucideIcon; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center rounded-xl border border-dashed border-slate-300 px-6 py-10 text-center dark:border-slate-700">
      {Icon && <div className="mb-3 rounded-full bg-slate-100 p-3 dark:bg-slate-800"><Icon className="h-6 w-6 text-slate-500" /></div>}
      <div className="font-medium">{title}</div>
      {children && <div className="mt-1 max-w-md text-sm muted">{children}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** Backwards-compatible simple empty block. */
export function Empty({ children }: { children: ReactNode }) {
  return <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm muted dark:border-slate-700">{children}</div>;
}

export function Stat({ label, value, hint, icon: Icon, tone }: { label: string; value: ReactNode; hint?: ReactNode; icon?: LucideIcon; tone?: "success" | "warning" | "danger" | "brand" }) {
  const toneCls = { success: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950", warning: "text-amber-600 bg-amber-50 dark:bg-amber-950", danger: "text-red-600 bg-red-50 dark:bg-red-950", brand: "text-brand-600 bg-brand-50 dark:bg-brand-900/40" }[tone ?? "brand"];
  return (
    <div className="card-pad">
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm muted">{label}</div>
        {Icon && <div className={`rounded-lg p-1.5 ${toneCls}`}><Icon className="h-4 w-4" /></div>}
      </div>
      <div className="mt-1 text-2xl font-semibold tracking-tight">{value}</div>
      {hint && <div className="mt-1 text-xs muted">{hint}</div>}
    </div>
  );
}

export function Card({ title, description, actions, children, className = "", bodyClass = "p-5" }: { title?: ReactNode; description?: ReactNode; actions?: ReactNode; children?: ReactNode; className?: string; bodyClass?: string }) {
  return (
    <section className={`card ${className}`}>
      {(title || actions) && (
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-800">
          <div className="min-w-0">
            {title && <h2 className="font-semibold">{title}</h2>}
            {description && <p className="mt-0.5 text-sm muted">{description}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={bodyClass}>{children}</div>
    </section>
  );
}

export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { id: T; label: string; count?: number }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="mb-5 flex gap-1 overflow-x-auto overflow-y-hidden border-b border-slate-200 dark:border-slate-800" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.id}
          role="tab"
          aria-selected={value === t.id}
          onClick={() => onChange(t.id)}
          className={`-mb-px flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition ${value === t.id ? "border-brand-600 text-brand-700 dark:border-brand-400 dark:text-brand-200" : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"}`}
        >
          {t.label}
          {t.count !== undefined && <span className="rounded-full bg-slate-100 px-1.5 text-[11px] text-slate-600 dark:bg-slate-800 dark:text-slate-300">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function Chips<T extends string>({ options, value, onChange }: { options: { id: T; label: string }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="flex flex-wrap gap-2" role="radiogroup">
      {options.map((o) => (
        <button
          key={o.id}
          role="radio"
          aria-checked={value === o.id}
          onClick={() => onChange(o.id)}
          className={`chip ${value === o.id ? "border-brand-600 bg-brand-600 text-white" : "border-slate-300 bg-white text-slate-600 hover:border-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Pager({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return <div className="px-4 py-3 text-xs muted">{total === 1 ? t("1 item") : t("{n} items", { n: fmtNum(total) })}</div>;
  return (
    <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm muted dark:border-slate-800">
      <span>{t("{n} items", { n: fmtNum(total) })}</span>
      <div className="flex items-center gap-2">
        <button className="btn-secondary btn-sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>{t("Previous")}</button>
        <span className="text-xs">{t("Page {page} of {pages}", { page, pages })}</span>
        <button className="btn-secondary btn-sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>{t("Next")}</button>
      </div>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, eyebrow }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; eyebrow?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        {eyebrow && <div className="mb-1 text-xs font-medium uppercase tracking-wide text-brand-600 dark:text-brand-400">{eyebrow}</div>}
        <h1 className="break-words text-2xl font-semibold tracking-tight">{title}</h1>
        {subtitle && <div className="mt-1.5 text-sm muted">{subtitle}</div>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Check({ ok, label, detail }: { ok: boolean | "warn"; label: ReactNode; detail?: ReactNode }) {
  const Icon = ok === true ? CheckCircle2 : ok === "warn" ? AlertTriangle : XCircle;
  const cls = ok === true ? "text-emerald-600" : ok === "warn" ? "text-amber-500" : "text-red-600";
  return (
    <div className="flex items-start gap-2.5 py-2">
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${cls}`} />
      <div className="min-w-0 text-sm">
        <div>{label}</div>
        {detail && <div className="text-xs muted">{detail}</div>}
      </div>
    </div>
  );
}

/** Small "?" button explaining a term in plain language (hover, focus or tap). */
export function Help({ children, label }: { children: ReactNode; label?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);
  return (
    <span ref={ref} className="relative inline-flex align-middle" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" className="rounded-full text-slate-400 hover:text-brand-600 focus:text-brand-600 focus:outline-none"
        aria-label={label ?? t("What does this mean?")} aria-expanded={open} onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }} onFocus={() => setOpen(true)} onBlur={() => setOpen(false)}>
        <HelpCircle className="h-3.5 w-3.5" />
      </button>
      {open && (
        <span role="tooltip" className="absolute left-1/2 top-full z-40 mt-1.5 w-64 -translate-x-1/2 rounded-lg bg-slate-900 px-3 py-2 text-left text-xs font-normal normal-case leading-relaxed tracking-normal text-white shadow-lg dark:bg-slate-700">
          {children}
        </span>
      )}
    </span>
  );
}
